import { useEffect } from 'react';

interface DocumentMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  url?: string;
}

/**
 * Custom hook to update document meta tags for SEO and social sharing
 * Updates <title>, <meta description>, and OpenGraph tags
 */
export const useDocumentMeta = ({
  title,
  description,
  ogImage = 'https://kempion.com/assets/og-image.png',
  url = window.location.href
}: DocumentMetaProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (property: string, content: string, isName = false) => {
      const attribute = isName ? 'name' : 'property';
      let tag = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, property);
        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description, true);

    // OpenGraph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:url', url);
    updateMetaTag('og:image', ogImage);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', ogImage, true);

    // Cleanup: restore original title on unmount
    return () => {
      document.title = 'Kempion Research — Decision Intelligence';
    };
  }, [title, description, ogImage, url]);
};
